import { Props } from './Notification';

export interface QueuedNotification {
  id: number;
  props: Props;
  resolve: (value: boolean) => void;
}

type Listener = (items: Array<QueuedNotification>) => void;

let items: Array<QueuedNotification> = [];
let nextId = 0;
const listeners = new Set<Listener>();

const emit = () => {
  listeners.forEach(listener => listener(items));
};

/**
 * The state behind Notification.create, kept in a plain module rather than a component.
 *
 * Notifications are raised from anywhere — stores, services, event handlers — so the
 * queue cannot live in the tree above its callers. NotificationHost subscribes to it and
 * renders the result through a portal, which is what replaced the per-notification
 * ReactDOM.render that React 18 deprecates.
 *
 * A Set of listeners rather than mobx: this holds no domain state, nothing observes it
 * but the host, and keeping it dependency-free means the queue is testable on its own.
 */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(items);

  return () => {
    listeners.delete(listener);
  };
}

export function enqueue(props: Props, resolve: (value: boolean) => void): number {
  const id = nextId;
  nextId += 1;
  items = [...items, { id, props, resolve }];
  emit();

  return id;
}

export function dequeue(id: number): void {
  items = items.filter(item => item.id !== id);
  emit();
}

/** Test helper: drops every queued notification without resolving it. */
export function reset(): void {
  items = [];
  emit();
}
